'use client'

import React, { useState } from 'react'

import { useAutoResizeTextarea } from './useAutoResizeTextarea'

import { Box, HTMLChakraProps, chakra } from '@chakra-ui/react'

import {
  ColorScheme,
  actionButtonInnerStyles,
  actionButtonWrapperStyles,
  multilineContainerStyles,
  multilineLabelStyles,
  multilineOuterStyles,
  multilineTextareaStyles,
  multilineWarningTextStyles,
} from './MultilineTextInput.styles'

export type { ColorScheme }

type OwnedTextareaProps =
  | 'value'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'disabled'
  | 'autoFocus'
  | 'placeholder'
  | 'width'

export type MultilineTextInputProps = Omit<
  HTMLChakraProps<'textarea'>,
  OwnedTextareaProps
> & {
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  colorScheme?: ColorScheme
  width?: string | number
  autoFocus?: boolean
  autoResize?: boolean
  autoResizeBoundaryRef?: React.RefObject<HTMLElement>
  autoResizeMaxRows?: number
  autoResizeViewportOffset?: number
  actionButton?: React.ReactNode
  label?: string
  rightLabel?: string
  isDisabled?: boolean
  outerPadding?: string | number
  hasSideBorders?: boolean
  /** Paints the error border and reveals `warningText`. */
  hasWarning?: boolean
  warningText?: string
}

export const MultilineTextInput = ({
  value,
  onChange,
  placeholder,
  colorScheme = 'dark',
  width = '100%',
  autoFocus,
  autoResize = false,
  autoResizeBoundaryRef,
  autoResizeMaxRows,
  autoResizeViewportOffset,
  actionButton,
  label,
  rightLabel,
  isDisabled = false,
  outerPadding = '20px',
  hasSideBorders = true,
  hasWarning = false,
  warningText,
  ...rest
}: MultilineTextInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useAutoResizeTextarea({
    boundaryRef: autoResizeBoundaryRef,
    enabled: autoResize,
    maxRows: autoResizeMaxRows,
    value,
    viewportOffset: autoResizeViewportOffset,
  })

  const input = (
    <Box
      {...multilineContainerStyles(colorScheme, isFocused, width, hasWarning)}
      opacity={isDisabled ? 0.5 : 1}
    >
      <chakra.textarea
        autoFocus={autoFocus}
        disabled={isDisabled}
        onBlur={() => setIsFocused(false)}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        placeholder={placeholder}
        ref={textareaRef}
        value={value}
        {...multilineTextareaStyles(colorScheme)}
        {...rest}
      />
      <Box
        {...actionButtonWrapperStyles(!!actionButton)}
        pointerEvents={isDisabled ? 'none' : undefined}
      >
        <Box {...actionButtonInnerStyles}>{actionButton}</Box>
      </Box>
      {hasWarning && warningText && (
        <Box
          alignSelf='flex-start'
          {...multilineWarningTextStyles(colorScheme)}
        >
          {warningText}
        </Box>
      )}
    </Box>
  )

  if (!label) return input

  return (
    <Box
      {...multilineOuterStyles(colorScheme, hasSideBorders)}
      p={outerPadding}
    >
      <Box alignItems='center' display='flex' justifyContent='space-between'>
        <Box as='span' {...multilineLabelStyles(colorScheme)}>
          {label}
        </Box>
        {rightLabel && (
          <Box as='span' {...multilineLabelStyles(colorScheme)}>
            {rightLabel}
          </Box>
        )}
      </Box>
      {input}
    </Box>
  )
}
